import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  FileButton,
  Group,
  Image,
  Loader,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import {
  IconCrop,
  IconLanguage,
  IconPhoto,
  IconSettings,
  IconUpload,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import {
  getBrand,
  updateBrand,
  uploadLoadingImage,
  uploadLogo,
  uploadMark,
  uploadTenantImage,
} from "@/api/tenant";
import { resizeToPng } from "@/lib/image";
import { notify } from "@/lib/notify";
import { ImageCropModal } from "@/components/ImageCropModal";
import { ImagePreview } from "@/components/ImagePreview";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import { ListError } from "@/components/DataList";
import { PageTitle } from "@/components/PageTitle";
import { ViewTransition } from "@/components/ViewTransition";
import { MenuLanguages } from "./MenuLanguages";
import { MenuSettings } from "./MenuSettings";

const MAX_LOGO_SIZE = 2 * 1024 * 1024;
// Kırpılan çıktı için AYRI tavan: kaynak 2 MB'ın altında elendi ama buradaki dosya bizim
// ürettiğimiz PNG ve şeffaflık alfa kanalı eklediği için kaynaktan büyük çıkabiliyor — kaynak
// sınırını tekrar uygulamak "görselin 2 MB'dan büyük" diyen yanlış bir mesajla çıkışsız bırakıyordu.
//
// Tavan API'nin 4 MB'ının ALTINDA (ImageUploadModal'daki MAX_UPLOAD_SIZE ile aynı kural): R2'ye
// giren her görsel Worker kotasına sayılıyor. 800×800 düz renkli bir logo PNG olarak 50-150 KB,
// yükleme ekranı 400×400 daha da küçük; 1 MB'ı ancak logo yuvasına fotoğraf konursa zorlar.
const MAX_OUTPUT_SIZE = 1024 * 1024;
// Menü bu görseli 80px'lik bir kutuda basıyor (menu/assets/css/loading.css); 400 retina ve
// 2x zoom için fazlasıyla yetiyor.
const LOADING_IMAGE_SIZE = 400;
const ACCEPTED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
];

// Üç çıktı da TEK kaynak görselden kadrajlanıyor. Sıra kasıtlı: menüde en görünür olan başta.
//
// Üçünün de çerçevesi KARE. Menü logosunda 2:1 ve 3:1 seçenekleri vardı; çoğu marka logosu simge +
// altında yazı biçiminde geliyor ve geniş oranlar yazıyı kesiyordu, kimse kullanmadı. Tek çerçeve
// kalınca oran seçicisi de gereksizleşti (bkz. ImageCropModal).
//
// width/height çıktının hedef ölçüsü — kırpılan alan daha küçükse büyütülmüyor (bkz. lib/image).
const OUTPUTS = [
  {
    key: "logo",
    title: "Menü Logosu",
    hint: "Menü başlığı",
    // Asıl yeri menü başlığı; ama Logo (kare) yoksa şube seçim kartı, sekme ikonu, menüdeki web
    // sitesi simgesi ve panelin kenar çubuğu da buna düşüyor (menu/helpers/favicon.js,
    // BranchPicker, BrandIcon, panel AppLayout).
    description:
      "Menü başlığında görünür. Logo yüklenmemişse şube seçim kartı, sekme ikonu ve panel de buna düşer.",
    aspect: { width: 800, height: 800 },
    emptyTip: "Menü başlığında logo yerine marka adı yazısı çıkar.",
    url: (brand) => brand?.logoUrl,
  },
  {
    key: "mark",
    title: "Logo",
    hint: "Sekme, ana ekran, şube seçimi",
    description:
      "Kare alan sekme ikonu ve ana ekran ikonu olarak da kullanılır — kenarlarda boşluk bırak.",
    aspect: { width: 512, height: 512 },
    emptyTip:
      "Sekme ikonu, ana ekran ikonu ve şube seçim kartı bunu bekliyor. Yoksa menü logosuna düşer.",
    url: (brand) => brand?.markUrl,
  },
  {
    key: "loading",
    title: "Yükleme Ekranı",
    hint: "Menü açılırken",
    // Diğer ikisinden AYRIŞTIĞI yer burası: menü bunu 80px'lik, 12px yuvarlatılmış bir kutuya
    // basıyor ve arkasında #f0f0f0 zemin + siyah halkalar var (menu/assets/css/loading.css).
    // Yani kadraj simgeye yakın olmalı ve içerik kenara dayanmamalı.
    //
    // Zemin ŞEFFAF kalıyor, dolgu rengi seçtirmiyoruz: #f0f0f0 üzerine basılan opak bir kare
    // logo değil rozet gibi duruyor, sayfa zemininden ayrışan bir dikdörtgen çıkıyor.
    description:
      "Menü açılırken 80px’lik yuvarlatılmış bir kutuda, açık gri zemin ve siyah halkaların ortasında görünür. Zemin şeffaf kalır.",
    aspect: { width: LOADING_IMAGE_SIZE, height: LOADING_IMAGE_SIZE },
    emptyTip:
      "Menü açılırken yalnızca dalga animasyonu görünür, görsel çıkmaz.",
    // Sabit anahtarda durduğu için URL değişmiyor; token önbelleği kırmak zorunda.
    url: (brand, token) =>
      brand?.hasLoadingImage ? `${brand.loadingImageUrl}?v=${token}` : null,
    plate: {
      // 12px yuvarlatma 80px'lik kutuda köşeleri yiyor; %10 içeri almak simgeyi kurtarıyor.
      padding: 0.1,
      label: "Menüde böyle görünecek",
      note: "Gerçek boyut 80px. Yazılı logo bu ölçüde okunmaz — simgeyi kadraja al.",
    },
  },
];

const formatSize = (bytes) => `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Sekme (64) ve ana ekran (180) ikonlarını Logo'dan (kare) üretip kaydeder. Kaynak YALNIZCA o —
// menü logosuna düşen eski yol kaldırıldı.
//
// Ana ekran ikonu beyaz zeminli: iOS şeffaflığı siyaha çeviriyor, logo siyah bir karenin içinde
// çıkardı. Favicon şeffaf kalıyor.
const deriveIcons = async (file) => {
  const derive = (size, bg) =>
    resizeToPng(file, size, bg).then(uploadTenantImage);
  // allSettled, all DEĞİL: /tenant/upload dosyayı R2'ye yazıp URL döndürüyor ama satıra hiçbir şey
  // yazmıyor. Promise.all biri düşünce updateBrand'i hiç çalıştırmıyordu, yani inen kardeş dosya
  // hiçbir satırın işaret etmediği ve hiçbir kodun silmeyeceği bir obje olarak R2'de kalıyordu.
  const [favicon, appleTouch] = await Promise.allSettled([
    derive(64),
    derive(180, "#ffffff"),
  ]);
  const icons = {
    ...(favicon.status === "fulfilled" && { favicon: favicon.value }),
    ...(appleTouch.status === "fulfilled" && { appleTouch: appleTouch.value }),
  };

  if (Object.keys(icons).length) {
    await updateBrand({ icons });
  }

  if (favicon.status === "rejected" || appleTouch.status === "rejected") {
    throw favicon.reason ?? appleTouch.reason;
  }
};

export default function BrandSettings() {
  const queryClient = useQueryClient();
  const {
    data: brand,
    isLoading,
    isError,
    refetch,
  } = useQuery({ queryKey: ["brand"], queryFn: getBrand });
  // The loading image lives at a fixed key, so a fresh upload would otherwise show the
  // cached old one here. Bumped on every successful upload to force a re-fetch.
  const [previewToken, setPreviewToken] = useState(0);

  // Kaynak görsel bellekte duruyor: sayfada kaldığı sürece üç çıktının hepsi ondan kadrajlanabilir.
  // Sunucuda saklanmıyor — sayfa yenilenince yeniden seçmek gerekiyor.
  const [source, setSource] = useState(null);
  // Kadrajlanan hedefin anahtarı. Dosyayı ayrıca tutmuyoruz — kaynak tek ve pencere açıkken
  // değiştirilemiyor, kopyasını taşımak iki yeri senkron tutma yükü demekti.
  const [cropKey, setCropKey] = useState(null);
  const { url: sourceUrl, failed: sourceFailed } = useObjectUrl(source);

  const refresh = (message) => () => {
    notify.success(message);
    setPreviewToken((token) => token + 1);
    queryClient.invalidateQueries({ queryKey: ["brand"] });
  };

  // Yalnızca menü logosunu yükler. Eskiden buradan hem yükleme ekranı görseli hem (kare logo
  // yoksa) favicon/ana ekran ikonu üretiliyordu; kullanıcının seçmediği bir kadraj sessizce üç
  // ayrı yere yazılıyordu. Artık her hedefin kadrajını kullanıcı seçiyor.
  const logoMutation = useMutation({
    mutationFn: uploadLogo,
    onSuccess: refresh("Menü logosu güncellendi"),
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "Menü logosu güncellenemedi"),
  });

  // Sekme ve ana ekran ikonu buradan üretiliyor — ikisinin ayrı bir yükleme alanı yok ve zaten
  // kullanıcının BU slot için seçtiği kadrajın yeniden boyutlandırılmışı. Başka bir görselin
  // yerine geçmiyor, o yüzden "otomatik" sayılmıyor.
  const markMutation = useMutation({
    mutationFn: async (file) => {
      await uploadMark(file);

      try {
        return await deriveIcons(file);
      } catch (error) {
        // Hangi aşamada düştüğümüzü çağırana taşı: tek bir onError iki await'i birden
        // kapsıyordu ve upload'ın KENDİSİ düştüğünde bile "Logo güncellendi ama…" diyordu.
        throw Object.assign(error, { markLanded: true });
      }
    },
    onSuccess: refresh("Logo güncellendi"),
    onError: (error) => {
      notify.error(
        error.markLanded
          ? "Logo güncellendi ama sekme/ana ekran ikonları üretilemedi"
          : (error.response?.data?.error ?? "Logo güncellenemedi"),
      );
      // Yükleme indiyse kart hâlâ eskisini gösteriyor; onError'da tazelemek şart.
      queryClient.invalidateQueries({ queryKey: ["brand"] });
    },
  });

  // Sabit `loading/<slug>.png` anahtarına yazılıyor — menü bu URL'i slug'dan kurup ilk boyamada
  // basıyor, API'ye sormuyor.
  const loadingMutation = useMutation({
    mutationFn: uploadLoadingImage,
    onSuccess: refresh("Yükleme görseli güncellendi"),
    onError: (error) =>
      notify.error(
        error.response?.data?.error ?? "Yükleme görseli yüklenemedi",
      ),
  });

  const mutations = {
    logo: logoMutation,
    mark: markMutation,
    loading: loadingMutation,
  };

  const withinSize = (file) => {
    if (file.size > MAX_LOGO_SIZE) {
      notify.warning("Görsel 2 MB'dan büyük olamaz");

      return false;
    }

    return true;
  };

  const outputWithinSize = (file) => {
    if (file.size > MAX_OUTPUT_SIZE) {
      notify.warning(
        "Kırpılan görsel çok büyük — kadrajı daraltıp (yakınlaştırarak) tekrar dene",
      );

      return false;
    }

    return true;
  };

  // FileButton'ın <input> değeri seçimden sonra dolu kalıyor: AYNI dosyayı ikinci kez seçmek
  // change olayı üretmiyor ve buton ölü görünüyordu. Yükleyip beğenmeyip aynı dosyayı tekrar
  // seçmek bu akışta sık, o yüzden her seçimden sonra input sıfırlanıyor.
  const resetSourcePicker = useRef(null);

  const pickSource = (file) => {
    resetSourcePicker.current?.();

    if (file && withinSize(file)) {
      setSource(file);
    }
  };

  const runUpload = async (key, file) => {
    if (!outputWithinSize(file)) {
      return;
    }
    try {
      await mutations[key].mutateAsync(file);
      setCropKey(null);
    } catch (error) {
      // Mutation'ın onError'ı zaten bildirdi; pencere açık kalsın ki kullanıcı yeniden denesin.
      // Ama mutation'dan GELMEYEN bir hata (ör. yanlış anahtar) buraya düşerse kimse haber
      // vermemiş olur; boş catch onu sonsuza kadar sessiz bir butona çevirirdi.
      if (!error?.isAxiosError) {
        console.error("[panel] upload path failed", key, error);
        notify.error("Beklenmeyen bir hata oluştu");
      }
    }
  };

  const output = cropKey ? OUTPUTS.find((item) => item.key === cropKey) : null;
  // Açılış sekmesi listedeki ilk sekmeyle aynı olmalı, yoksa sayfa ikinci sekme seçili açılıyor.
  const [tab, setTab] = useState("languages");

  return (
    <Stack gap="lg">
      <PageTitle>Ayarlar</PageTitle>
      <Group>
        <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
          <IconSettings size={26} stroke={1.6} />
        </ThemeIcon>
        <div>
          <Title order={2}>Ayarlar</Title>
          <Text c="dimmed" size="sm">
            Markanın menüde nasıl göründüğünü buradan yönet
          </Text>
        </div>
      </Group>

      {/* Sekmeler, kartların alt alta dizilmesi yerine: ayar sayısı artıyor (görseller,
                diller, sırada başkaları) ve hepsi tek sütunda uzayınca sayfa kaydırılmadan
                görülemiyor. Şube Düzenle penceresiyle aynı kalıp — iki yerde aynı iş, aynı görünüm. */}
      {isError && (
        <ListError title="Marka ayarları alınamadı" onRetry={refetch} />
      )}

      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="languages" leftSection={<IconLanguage size={16} />}>
            Menü Dilleri
          </Tabs.Tab>
          <Tabs.Tab value="images" leftSection={<IconPhoto size={16} />}>
            Marka Görselleri
          </Tabs.Tab>
        </Tabs.List>

        <ViewTransition transitionKey={tab}>
          <Tabs.Panel value="images">
            <Card withBorder radius="md" p="lg">
              <Stack gap="lg">
                <div>
                  <Text fw={600}>Marka Görselleri</Text>
                  <Text size="xs" c="dimmed">
                    Tek görsel yükle, her kullanım için kadrajını ayrı seç
                  </Text>
                </div>

                <Group align="flex-start" wrap="wrap" gap="lg">
                  <Box w={200}>
                    {/* Dropzone önizlemenin KENDİSİ: seçili görsel varsa onu gösteriyor,
                                üstüne yeni dosya bırakmak kaynağı değiştiriyor. Ayrı bir önizleme
                                kutusu + ayrı bir bırakma alanı aynı işi iki kez kaplıyordu. */}
                    <Dropzone
                      onDrop={(files) => pickSource(files[0])}
                      onReject={() =>
                        notify.warning("Desteklenmeyen dosya ya da 2 MB üstü")
                      }
                      accept={ACCEPTED_TYPES}
                      maxSize={MAX_LOGO_SIZE}
                      p={0}
                      style={{ borderRadius: "var(--mantine-radius-md)" }}
                    >
                      <ImagePreview
                        src={sourceUrl}
                        alt="Kaynak görsel"
                        height={150}
                        empty="Görseli buraya bırak ya da tıkla"
                      />
                    </Dropzone>
                  </Box>

                  <Stack gap={6} style={{ flex: 1, minWidth: 220 }}>
                    <Text fw={600} size="sm">
                      Kaynak Görsel
                    </Text>
                    {sourceFailed ? (
                      <Text size="xs" c="red">
                        Bu dosya okunamadı — taşınmış ya da silinmiş olabilir.
                        Yeniden seç.
                      </Text>
                    ) : source ? (
                      <Text size="xs" c="dimmed">
                        {source.name} · {formatSize(source.size)}
                      </Text>
                    ) : (
                      <Text size="xs" c="dimmed">
                        En büyük halini yükle — aşağıdaki her çıktı bundan
                        kadrajlanır. PNG, JPG, WebP ya da SVG, en fazla 2 MB.
                      </Text>
                    )}

                    <Group gap="xs" mt={4}>
                      <FileButton
                        onChange={pickSource}
                        accept={ACCEPTED_TYPES.join(",")}
                        resetRef={resetSourcePicker}
                      >
                        {(props) => (
                          <Button
                            {...props}
                            size="xs"
                            variant={source ? "light" : "filled"}
                            leftSection={<IconUpload size={14} />}
                          >
                            {source ? "Görseli Değiştir" : "Görsel Seç"}
                          </Button>
                        )}
                      </FileButton>
                      {source && (
                        <Button
                          size="xs"
                          variant="subtle"
                          color="gray"
                          onClick={() => setSource(null)}
                        >
                          Temizle
                        </Button>
                      )}
                    </Group>

                    {source && (
                      <Text size="xs" c="dimmed" mt={4}>
                        Kaynak yalnızca bu sayfa açıkken tutuluyor; sayfayı
                        yenilersen yeniden seç.
                      </Text>
                    )}
                  </Stack>

                  {/* Türetilen ikonlar bir çıktı değil, sonuç — kartın en altında ayrı bir
                            bölüm olarak durunca dördüncü bir yükleme alanı gibi okunuyordu.
                            Sağ üstte, kaynağın hizasında bir durum göstergesi. */}
                  {(brand?.icons?.favicon || brand?.icons?.appleTouch) && (
                    <Stack gap={6} align="flex-end">
                      <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                        Üretilen ikonlar
                      </Text>
                      <Group gap="md">
                        {brand.icons.favicon && (
                          <Stack gap={2} align="center">
                            <Image
                              src={brand.icons.favicon}
                              w={28}
                              h={28}
                              radius="sm"
                            />
                            <Text size="xs" c="dimmed">
                              Favicon
                            </Text>
                          </Stack>
                        )}
                        {brand.icons.appleTouch && (
                          <Stack gap={2} align="center">
                            <Image
                              src={brand.icons.appleTouch}
                              w={40}
                              h={40}
                              radius="md"
                            />
                            <Text size="xs" c="dimmed">
                              Ana ekran
                            </Text>
                          </Stack>
                        )}
                      </Group>
                    </Stack>
                  )}
                </Group>

                <Divider label="Çıktılar" labelPosition="center" />

                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                  {OUTPUTS.map((item) => {
                    // Rozet tek bir şey söylüyor: bu slot dolu mu. Otomatik türetme
                    // kalktığı için üçü de aynı kuralda — boşken ne olacağını tooltip
                    // anlatıyor (OUTPUTS.emptyTip).
                    const url = item.url(brand, previewToken);

                    return (
                      <Stack key={item.key} gap={6}>
                        <Group justify="space-between" wrap="nowrap" gap="xs">
                          <div style={{ minWidth: 0 }}>
                            <Text fw={600} size="sm" truncate>
                              {item.title}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>
                              {item.hint}
                            </Text>
                          </div>
                          <Tooltip
                            label={
                              url
                                ? "Kendi kadrajın kullanılıyor."
                                : item.emptyTip
                            }
                            withArrow
                            multiline
                            w={250}
                          >
                            <Badge
                              variant="light"
                              color={url ? "teal" : "gray"}
                              size="sm"
                            >
                              {url ? "Yüklü" : "Yok"}
                            </Badge>
                          </Tooltip>
                        </Group>

                        {isLoading ? (
                          <Group justify="center" h={130}>
                            <Loader size="sm" />
                          </Group>
                        ) : (
                          <ImagePreview
                            src={url}
                            alt={item.title}
                            height={130}
                            empty="Henüz yüklenmedi"
                          />
                        )}

                        <Tooltip
                          label={
                            source
                              ? item.description
                              : "Önce yukarıdan bir kaynak görsel seç"
                          }
                          withArrow
                          multiline
                          w={240}
                        >
                          <Button
                            size="xs"
                            variant="light"
                            disabled={!source}
                            loading={mutations[item.key].isPending}
                            leftSection={<IconCrop size={14} />}
                            onClick={() => setCropKey(item.key)}
                          >
                            Kadrajla ve Yükle
                          </Button>
                        </Tooltip>
                      </Stack>
                    );
                  })}
                </SimpleGrid>
              </Stack>
            </Card>
          </Tabs.Panel>

          {/* Dil listesi ve açılış ekranı AYNI sekmede ama AYRI kartlarda: ikisi de
                        dil hakkında, ama biri "Kaydet"e basınca gidiyor, diğeri anahtara basınca.
                        Tek kartta iki kaydetme davranışı, hangisinin ne zaman yazdığını
                        okunmaz hale getirirdi. */}
          <Tabs.Panel value="languages">
            <Stack gap="lg">
              <MenuLanguages />
              <MenuSettings />
            </Stack>
          </Tabs.Panel>
        </ViewTransition>
      </Tabs>

      {output && (
        <ImageCropModal
          onClose={() => setCropKey(null)}
          file={source}
          title={output.title}
          description={output.description}
          aspect={output.aspect}
          plate={output.plate}
          confirming={mutations[cropKey].isPending}
          onConfirm={(cropped) => runUpload(cropKey, cropped)}
        />
      )}
    </Stack>
  );
}
